import { graphql } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useEffect, useMemo, useState } from 'react';
import SimpleReactLightbox from 'simple-react-lightbox'; // Used for Pinouts
import SwiperCore, { Navigation, Pagination } from 'swiper';
import Layout from '../components/Layout';
import { TabsHeader } from '../components/TabsHeader/TabsHeader';
import ProductHeader from '../components/product/ProductHeader';
import SEO from '../components/seo';

import Breadcrumbs from '../components/Breadcrumbs/Breadcrumbs';
import ConnectAndContribute from '../components/ConnectAndContribute/ConnectAndContribute';
import DownloadableResources from '../components/DownloadableResource/DownloadableResources';
//import SideBar from '../components/SideBar/SideBar';
import { Sidebar } from '../entities/Sidebar';
import { imageFor, isInside } from '../utils/functions';
import './product.scss';
//import Feedback from '../components/Feedback/Feedback';
import ProductCompatibility from '../components/product/ProductCompatibility';
import ProductFeatures from '../components/product/ProductFeatures';
import ProductSuggestedLibraries from '../components/product/ProductSuggestedLibraries';
import ProductTechSpec from '../components/product/ProductTechSpec';
import ProductTutorials from '../components/product/ProductTutorials';

import 'swiper/components/navigation/navigation.scss';
import 'swiper/components/pagination/pagination.scss';
import 'swiper/swiper-bundle.css';
import { BACK, PRO, SIDEBAR_HARDWARE } from '../utils/constants';
import TutorialSideBar from '../components/SideBar/TutorialSidebar';

// inizialize SwiperCore
SwiperCore.use([Navigation, Pagination]);

interface ProductProps {
  pageContext: any;
  data: {
    families: {
      edges: Array<any>;
    };
    categories: {
      edges: Array<any>;
    };
    images: {
      edges: Array<any>;
    };
    products: {
      edges: Array<any>;
    };
    product: {
      body: string;
      fields: any;
      childMdx: any;
      frontmatter: {
        name: string;
        url_shop: string;
        url_guide: string;
        hasSetupGuide: boolean;
        core: string;
        certifications: Array<any>;
        forumCategorySlug: string;
        status: string;
        primary_button_url?: string;
        primary_button_title?: string;
        secondary_button_url?: string;
        secondary_button_title?: string;
      };
    };
    image: IGatsbyImageData | string;
    features: {
      childMdx: {
        body: string;
      };
    };
    techspecs: any;
    techSpecInfo: {
      childMdx: any;
    };
    revision: {
      childMdx: any;
    };
    compatibility: {
      fields: {
        content: string;
      };
    };
    downloads: any;
    essentials: any;
    tutorials: any;
    interactive: any;
    troubleshooting: any;
    getInspired: any;
    suggestions: any;
    previousDocumentation: {
      edges: Array<any>;
    };
  };
}

export default function Product({ data }: ProductProps): React.ReactElement {
  //const isDesktop = typeof window !== 'undefined' ? window.innerWidth > 768 : true;
  const [domLoaded, setDomLoaded] = useState(true);

  useEffect(() => {
    setDomLoaded(true);
  }, []);
  //const isDesktop = typeof window !== 'undefined' ? window.innerWidth > 768 : true;

  function getProductDataRefactored(data: any) {
    let refactoredData: Sidebar[] = [];

    data?.families.edges.forEach((family) => {
      const categories = data.categories.edges.filter((category) => isInside(category, family));
      const images = imageFor(data.images, family);

      const nestedCat = categories
        .sort((a, b) => (a.node.relativeDirectory < b.node.relativeDirectory ? -1 : 1))
        .map((category) => {
          const nestedChild = data.products.edges
            .filter((product) => isInside(product, category))
            .sort((a, b) => (a.node.relativeDirectory < b.node.relativeDirectory ? -1 : 1))
            .map((product) => {
              if (product.node.childMdx) {
                return {
                  title: product.node.childMdx.frontmatter.title,
                  url: product.node.fields.slug,
                };
              } else return null;
            });
          return { category: category.node.childMdx.frontmatter.title, nestedChildren: nestedChild };
        });
      if (family.node.childMdx) {
        refactoredData.push({
          firstLevelCategory: family.node.childMdx.frontmatter.title,
          image: images,
          nestedCategories: nestedCat,
        });
      }
    });

    return refactoredData;
  }

  const productRefactoredData = useMemo(() => {
    return getProductDataRefactored(data);
  }, [data]);

  const tabsData = useMemo(() => {
    return [
      {
        title: 'Features',
        url: 'features',
        children: data?.features?.childMdx ? <ProductFeatures features={data.features.childMdx} /> : null,
      },
      {
        title: 'Tutorials',
        url: 'tutorials',
        children:
          data.tutorials.edges.length > 0 ? <ProductTutorials tutorials={data.tutorials} product={data.product.childMdx.frontmatter.title} /> : null,
      },
      {
        title: 'Tech Specs',
        url: 'tech-specs',
        children: data.techSpecInfo?.childMdx ? (
          <ProductTechSpec
            info={data.techSpecInfo?.childMdx}
            techspecs={data.techspecs}
            certifications={data.product.childMdx.frontmatter?.certifications}
          />
        ) : null,
      },
      {
        title: 'Compatibility',
        url: 'compatibility',
        children: data.compatibility ? <ProductCompatibility compatibility={data.compatibility.fields.content} /> : null,
      },
      {
        title: 'Suggested Libraries',
        url: 'suggested-libraries',
        children: data.essentials?.childMdx ? (
          <ProductSuggestedLibraries essentials={data.essentials?.childMdx} openGettingStartedHandler={null}>
            {data.essentials?.childMdx.body}
          </ProductSuggestedLibraries>
        ) : null,
      },
    ];
  }, [data]);

  const isProProduct = useMemo(() => {
    let found = false;
    data.families.edges.forEach((family) => {
      if (family.node.childMdx.frontmatter.businessUnit === PRO) {
        productRefactoredData.forEach((product) => {
          if (family.node.childMdx.frontmatter.title === product.firstLevelCategory) {
            product.nestedCategories.forEach((cat) => {
              cat.nestedChildren.forEach((category) => {
                if (category && category.title === data.product.childMdx.frontmatter.title) {
                  found = true;
                  return found;
                }
              });
            });
          }
        });
      }
    });
    return found;
  }, [data.families.edges, data.product.childMdx.frontmatter.title, productRefactoredData]);

  return (
    <>
      {domLoaded && (
        <Layout>
          {data.product.childMdx?.frontmatter?.title && (
            <SEO
              title={data.product.childMdx.frontmatter.title}
              description={data.product.childMdx.excerpt}
              image={data.image as IGatsbyImageData}
              isProductSvg={true}
            />
          )}
          <main className="page-container">
            {/* <SideBar
              links={[]}
              nestedCategories={productRefactoredData}
              selectedItem={data.product.childMdx.frontmatter.title}
              actualPage={SIDEBAR_HARDWARE}
              prevPage={{ title: BACK, pathname: '/hardware/' }}
            /> */}
            <TutorialSideBar
              links={[]}
              nestedCategories={productRefactoredData}
              selectedItem={data.product.childMdx.frontmatter.title}
              actualPage={SIDEBAR_HARDWARE}
              prevPage={{ title: BACK, pathname: '/hardware/' }}
            />
            <div className="page-layout product">
              <SimpleReactLightbox>
                <Breadcrumbs
                  actualPage={data.product.childMdx.frontmatter.title}
                  list={[
                    { title: 'Home', url: '/' },
                    { title: 'Hardware', url: '/hardware/' },
                    { title: data.product.childMdx.frontmatter.title, url: data.product.fields.slug },
                  ]}
                />
                <div className="product__layout">
                  <ProductHeader
                    product={data.product.childMdx}
                    image={data.image as IGatsbyImageData}
                    interactive={data.interactive}
                    purchaseLink={data.product.childMdx.frontmatter?.url_shop}
                  />
                  <DownloadableResources downloads={data.downloads} />
                  <TabsHeader tabs={tabsData} activeTab="Features" />
                </div>
              </SimpleReactLightbox>
              <ConnectAndContribute
                showContactButton={isProProduct}
                forumLink={data.product.childMdx.frontmatter.forumCategorySlug}
                projectHubLink={data.product.childMdx.frontmatter.title}
              />
              {/* <Feedback /> */}
            </div>
          </main>
        </Layout>
      )}
    </>
  );
}

export const pageQuery = graphql`query ($directory: String!, $downloadsDirectory: String!, $previousDocumentationDirectory: String!, $interactiveDirectory: String!, $product: String!) {
  product: file(relativeDirectory: {eq: $directory}, base: {eq: "product.md"}) {
    fields {
      category
      slug
    }
    childMdx {
      body
      excerpt(pruneLength: 200)
      frontmatter {
        title
        url_shop
        url_guide
        hasSetupGuide
        core
        certifications
        forumCategorySlug
        status
        primary_button_url
        primary_button_title
        secondary_button_url
        secondary_button_title
      }
    }
  }
  image: file(
    relativeDirectory: {eq: $directory}
    base: {regex: "/\\image.(jpe?g|png|gif|bmp|svg)$/i"}
  ) {
    childImageSharp {
      gatsbyImageData(layout: FIXED, width: 354, quality: 90, placeholder: BLURRED)
    }
    extension
    publicURL
  }
  features: file(relativeDirectory: {eq: $directory}, base: {eq: "features.md"}) {
    childMdx {
      body
    }
  }
  techSpecInfo: file(
    relativeDirectory: {eq: $directory}
    base: {eq: "tech-specs.md"}
  ) {
    childMdx {
      body
    }
  }
  techspecs: file(
    relativeDirectory: {eq: $directory}
    base: {eq: "tech-specs.yml"}
  ) {
    fields {
      content
    }
  }
  revision: file(relativeDirectory: {eq: $directory}, base: {eq: "revision.md"}) {
    childMdx {
      body
    }
  }
  compatibility: file(
    relativeDirectory: {eq: $directory}
    base: {eq: "compatibility.yml"}
  ) {
    fields {
      content
    }
  }
  essentials: file(
    relativeDirectory: {eq: $directory}
    base: {eq: "essentials.md"}
  ) {
    childMdx {
      body
    }
  }
  suggestions: file(
    relativeDirectory: {eq: $directory}
    base: {eq: "suggestions.md"}
  ) {
    childMdx {
      body
    }
  }
  downloads: allFile(filter: {relativeDirectory: {eq: $downloadsDirectory}}) {
    edges {
      node {
        base
        publicURL
      }
    }
  }
  previousDocumentation: allFile(
    filter: {relativeDirectory: {eq: $previousDocumentationDirectory}}
  ) {
    edges {
      node {
        base
        name
        publicURL
      }
    }
  }
  interactive: allFile(filter: {relativeDirectory: {eq: $interactiveDirectory}}) {
    edges {
      node {
        base
        publicURL
      }
    }
  }
  tutorials: allFile(
    filter: {fields: {product: {eq: $product}}}
    sort: {relativeDirectory: ASC}
  ) {
    edges {
      node {
        fields {
          slug
          gitLogLatestDate
        }
        childMdx {
          frontmatter {
            title
            description
            tags
            author
            difficulty
          }
        }
      }
    }
  }
  families: allFile(filter: { base: { eq: "family.md" } }, sort: { fields: { order: ASC } }) {
    edges {
      node {
        relativeDirectory
        childMdx {
          frontmatter {
            title
            description
            businessUnit
            anchor
          }
        }
      }
    }
  }
  categories: allFile(filter: { base: { eq: "category.md" } }) {
    edges {
      node {
        relativeDirectory
        childMdx {
          frontmatter {
            title
          }
        }
      }
    }
  }
  products: allFile(filter: { base: { eq: "product.md" } }) {
    edges {
      node {
        relativeDirectory
        fields {
          slug
          category
        }
        childMdx {
          frontmatter {
            title
            core
          }
        }
      }
    }
  }
  images: allFile(filter: { base: { eq: "image.png" } }) {
    edges {
      node {
        relativeDirectory
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED)
        }
      }
    }
  }
}`;
